import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useState } from "react"
import type {
	TChildAgeBand,
	TChildAgeBandOccupancy,
	TChildAgesFormValues,
	THotelDetailsFormValues,
	TNewHotelFormValues,
} from "./NewItem"
import type {
	THotelOtaChildAge,
	THotelOtaChildAgePaxType,
	THotelRemotePaxTypes,
	TOtaChildAgesPayload,
} from "./store"
import {
	addUnit,
	formatDate,
	parseDate,
	timeToUTCString,
} from "@sembark-travel/datetime-utils"
import config from "../config"
import { OTA_BASE_CHILD_OCCUPANCY } from "../HotelOtaRoomTypes"
import * as Validator from "yup"
import type { ObjectShape } from "yup"
import { PhoneNumberValidator, PincodeValidator } from "@sembark-travel/ui-form"
import { imageFileValidator } from "../shared/utils"

interface UseMergeEntitiesProps<T> {
	mergeUrl: string
	getId: (item: T) => number
	onSuccess: (primary: T) => void
}

export function useMergeEntities<T>({
	mergeUrl,
	getId,
	onSuccess,
}: UseMergeEntitiesProps<T>) {
	const xhr = useXHR()
	const [resetKey, setResetKey] = useState(0)

	const merge = async (
		primary: T,
		duplicates: T[],
		confirm: () => Promise<boolean>,
		entityLabel: string
	) => {
		try {
			if (!(await confirm())) return

			await xhr.post(mergeUrl, {
				primary: getId(primary),
				duplicates: duplicates.map(getId),
			})

			const mergeMore = window.confirm(
				`Do you want to merge more duplicate ${entityLabel}s?`
			)

			if (mergeMore) {
				setResetKey((k) => k + 1)
			} else {
				onSuccess(primary)
			}
		} catch (error) {
			const e = error as Error
			alert(e.message || "Something went wrong")
		}
	}

	return { merge, resetKey }
}

export function isPrimaryRemoved<T>(
	primary: T | undefined,
	duplicates: T[],
	getId: (item: T) => string | number
) {
	if (!primary) return false

	return duplicates.every((item) => getId(item) !== getId(primary))
}

// The API keeps stars as a string, but the forms only allow a 1-5 rating
export const HOTEL_STAR_OPTIONS = ["1", "2", "3", "4", "5"]

// Older hotels may carry free text (e.g. "Premium"), which the select can't show
export function toStarsFormValue(stars?: string | null) {
	return stars && HOTEL_STAR_OPTIONS.includes(stars) ? stars : undefined
}

// Links are allowed without a protocol (e.g. "maps.app.goo.gl/abc"), https is assumed
export function withUrlProtocol(url: string) {
	const trimmed = url.trim()
	return !trimmed || /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(trimmed)
		? trimmed
		: `https://${trimmed}`
}

// Fields validated by both the create hotel and the edit hotel forms
export function hotelDetailsSchemaShape({
	requireTripDestinations,
	requireImage,
}: {
	requireTripDestinations: boolean
	requireImage: boolean
}): ObjectShape {
	return {
		name: Validator.string()
			.required("Hotel name is required")
			.max(191, "Please use 191 or fewer characters"),
		stars: Validator.string()
			.required("Stars are required")
			.oneOf(HOTEL_STAR_OPTIONS, "Please select stars between 1 and 5"),
		brand_code: Validator.string()
			.required("Brand code is required")
			.max(4, "Please use 4 or fewer characters")
			.matches(/^[A-Z0-9]+$/, {
				message: "Use only capital letters and digits",
				excludeEmptyString: true,
			}),
		currency: Validator.string().required("Please select a currency"),
		location: Validator.mixed()
			.nullable()
			.required("Please provide location for hotel"),
		trip_destinations: requireTripDestinations
			? Validator.array()
					.required("Please select at least one destination")
					.min(1, "Please select at least one destination")
			: Validator.array().nullable(),
		image: imageFileValidator(
			requireImage ? "Please select a hotel image" : undefined
		),
		address: Validator.object().shape({
			line_1: Validator.string()
				.required("Street address is required")
				.max(100, "Please use 100 or less characters."),
			line_2: Validator.string()
				.required("Locality/Area is required")
				.max(100, "Please use 100 or less characters."),
			landmark: Validator.string()
				.nullable()
				.max(100, "Please use 100 or less characters."),
			postcode: PincodeValidator().required("Pincode is required"),
			city: Validator.mixed().nullable().required("Please select a city"),
			state: Validator.mixed().nullable().required("Please select a state"),
			country: Validator.mixed().nullable().required("Please select a country"),
		}),
		checkin_at: Validator.mixed().required("Check-in time is required"),
		checkout_at: Validator.mixed().required("Check-out time is required"),
		maps_url: Validator.string()
			.optional()
			.test("valid-url", "Please enter a valid URL", (value) =>
				value
					? Validator.string().url().isValidSync(withUrlProtocol(value))
					: true
			),
		amenities: Validator.array()
			.required("Please select at least one amenity")
			.min(1, "Please select at least one amenity"),
	}
}

/**
 * Validation schema of each step, in order. Removing the last item of a field
 * array sets it to `undefined` (not `[]`), so the lists that need at least one
 * item must be `required` too.
 */
export function newHotelStepSchemas({
	requireTripDestinations,
}: {
	requireTripDestinations: boolean
}) {
	const stepFields: Array<ObjectShape> = [
		{
			...hotelDetailsSchemaShape({
				requireTripDestinations,
				requireImage: true,
			}),
			contacts: Validator.array().of(
				Validator.object().shape({
					name: Validator.string()
						.required("Contact name is required")
						.max(191, "Please use 191 or fewer characters"),
					email: Validator.string()
						.email("Please enter a valid email")
						.optional(),
					phone_numbers: Validator.array()
						.of(PhoneNumberValidator())
						.required("Please provide a contact number")
						.min(1, "Please provide a contact number"),
				})
			),
		},
		{
			ota_room_types: Validator.array()
				.required("Please add at least one room type")
				.min(1, "Please add at least one room type"),
		},
		{
			ota_meal_plans: Validator.array()
				.required("Please add at least one meal plan")
				.min(1, "Please add at least one meal plan"),
		},
		{
			// optional, the hotel may not be connected to any channel manager
			channel_manager: Validator.object().nullable(),
		},
		{
			child_ages: childAgesValidationSchema,
		},
	]
	return {
		steps: stepFields.map((fields) => Validator.object().shape(fields)),
		all: Validator.object().shape(Object.assign({}, ...stepFields)),
	}
}

function toLocalTime(time: Date | string) {
	return formatDate(parseDate(time, config.timeFormat), config.timeFormat)
}

/**
 * The check-in and check-out times, as the api hold them: `*_at` in utc and
 * `*_at_local` on the hotel's own clock.
 *
 * The fields show the local time, so both have to be sent. Sending only the
 * utc one would leave a stale `*_at_local` behind, and that is the one read
 * back for display, so the edit would look like it never happened.
 */
function toApiTimes(prefix: "checkin" | "checkout", time: Date | string) {
	return {
		[`${prefix}_at`]: timeToUTCString(time),
		[`${prefix}_at_local`]: toLocalTime(time),
	}
}

export function hotelDetailsPayload(values: THotelDetailsFormValues) {
	const { address } = values
	return {
		name: values.name.trim(),
		stars: values.stars,
		brand_code: values.brand_code,
		currency: values.currency,
		location: values.location?.name,
		trip_destinations: (values.trip_destinations || []).map((t) => t.id),
		image: values.image,
		...toApiTimes("checkin", values.checkin_at),
		...toApiTimes("checkout", values.checkout_at),
		address: {
			line_1: address.line_1,
			line_2: address.line_2,
			landmark: address.landmark,
			postcode: address.postcode,
			location: address.city?.name,
			city: address.city?.name,
			state: address.state?.name,
			country: address.country?.name,
		},
		amenities: values.amenities.map(({ key, name }) => ({ key, name })),
		maps_url: values.maps_url
			? withUrlProtocol(values.maps_url)
			: values.maps_url,
	}
}

/**
 * The create hotel request body, before it is turned into FormData.
 * Expects values that passed all the steps' validations.
 */
function newHotelPayload(
	values: TNewHotelFormValues,
	remotePaxTypes?: TRemotePaxTypeDefaults
) {
	const { child_ages } = values
	const { aweb_remote_pax_type, ages } = formValuesToOtaChildAgesPayload(
		child_ages,
		remotePaxTypes
	)
	return {
		...hotelDetailsPayload(values),
		channel_manager: values.channel_manager?.id || null,
		extra_bed_child_age_start: child_ages.min_age,
		extra_bed_child_age_end: child_ages.max_age,
		contacts: values.contacts.map((contact) => ({
			name: contact.name.trim(),
			email: contact.email?.trim() || null,
			phone_numbers: contact.phone_numbers
				.filter((p) => p.number)
				.map((p) => ({
					number: p.number.replace(/[^\d]/gi, ""),
					country_code: p.country_code,
					is_primary: p.is_primary ? 1 : 0,
				})),
		})),
		ota_meal_plans: values.ota_meal_plans.map(
			({ name, description, meal_category }) => ({
				name,
				description,
				meal_category,
			})
		),
		ota_room_types: values.ota_room_types.map((roomType) => ({
			name: roomType.name.trim(),
			base_adult_occupancy: Number(roomType.base_adult_occupancy),
			base_child_occupancy: OTA_BASE_CHILD_OCCUPANCY,
			max_occupancy: Number(roomType.max_occupancy),
			max_adult_occupancy: Number(roomType.max_adult_occupancy),
			max_child_occupancy: Number(roomType.max_child_occupancy),
			no_of_rooms: Number(roomType.no_of_rooms),
			// specific meal plans can only be picked after the hotel is created
			includes_all_meal_plans: true,
			image: roomType.image,
			amenities: roomType.amenities.map(({ key, name }) => ({ key, name })),
		})),
		aweb_remote_pax_type,
		child_ages: ages,
	}
}

// Nested values use bracket keys (`address[line_1]`, `ota_room_types[0][image]`),
// booleans are sent as 1/0 and nulls as "" (the api reads empty strings as null).
function appendToFormData(data: FormData, key: string, value: unknown) {
	if (value === undefined) return
	if (value === null) {
		data.append(key, "")
	} else if (value instanceof Blob) {
		data.append(key, value)
	} else if (Array.isArray(value)) {
		value.forEach((item, index) =>
			appendToFormData(data, `${key}[${index}]`, item)
		)
	} else if (typeof value === "boolean") {
		data.append(key, value ? "1" : "0")
	} else if (typeof value === "object") {
		Object.entries(value).forEach(([childKey, childValue]) =>
			appendToFormData(data, key ? `${key}[${childKey}]` : childKey, childValue)
		)
	} else {
		data.append(key, String(value))
	}
}

/**
 * Request body as multipart form data, for the endpoints that take an image
 * along with the rest of the fields.
 */
function payloadToFormData(payload: object): FormData {
	const data = new FormData()
	appendToFormData(data, "", payload)
	return data
}

// Pass the channel manager's `remotePaxTypes` to send the child age mappings
export function buildNewHotelFormData(
	values: TNewHotelFormValues,
	remotePaxTypes?: TRemotePaxTypeDefaults
): FormData {
	return payloadToFormData(newHotelPayload(values, remotePaxTypes))
}

export const CHILD_AGE_BAND_OCCUPANCIES: Array<{
	value: TChildAgeBandOccupancy
	label: string
	paxTypes: Array<THotelOtaChildAgePaxType>
}> = [
	{ value: "CNB", label: "CNB", paxTypes: ["CNB"] },
	{ value: "CWEB", label: "CWEB", paxTypes: ["CWEB"] },
	{ value: "CNB_CWEB", label: "CNB + CWEB", paxTypes: ["CNB", "CWEB"] },
]

const DEFAULT_MIN_AGE = 6
const DEFAULT_MAX_AGE = 14

export function isWholeNumber(value: unknown): value is number {
	return typeof value === "number" && Number.isInteger(value)
}

export function occupancyPaxTypes(
	occupancy: TChildAgeBandOccupancy | undefined
): Array<THotelOtaChildAgePaxType> {
	return (
		CHILD_AGE_BAND_OCCUPANCIES.find((o) => o.value === occupancy)?.paxTypes ||
		[]
	)
}

type TRemotePaxTypeDefaults = Pick<
	THotelRemotePaxTypes,
	"default_adult_remote_pax_type" | "default_child_remote_pax_type"
>

// A band holds its CNB and CWEB mappings in separate fields
export function bandRemotePaxTypeField(
	paxType: THotelOtaChildAgePaxType
): "cnb_remote_pax_type" | "cweb_remote_pax_type" {
	return paxType === "CNB" ? "cnb_remote_pax_type" : "cweb_remote_pax_type"
}

/**
 * The remote pax type a band's pax type is sold as: its mapping, or the channel
 * manager's default when it is not mapped.
 */
export function bandRemotePaxType(
	band: TChildAgeBand,
	paxType: THotelOtaChildAgePaxType,
	defaults: TRemotePaxTypeDefaults
): string | null {
	return (
		band[bandRemotePaxTypeField(paxType)] ||
		defaults.default_child_remote_pax_type
	)
}

// Only a mapping that differs from the channel manager's default is stored
export function mappedRemotePaxType(
	value: string | undefined,
	defaultValue: string | null
): string | null {
	return value && value !== defaultValue ? value : null
}

export function bandStartAges(
	minAge: number | undefined,
	bands: Array<TChildAgeBand>
): Array<number | undefined> {
	let start = isWholeNumber(minAge) ? minAge : undefined
	return bands.map((band) => {
		const bandStart = start
		start = isWholeNumber(band.end_age) ? band.end_age + 1 : undefined
		return bandStart
	})
}

// A saved hotel's children age range and its child ages, one row per pax type,
// so an age band allowing both CNB and CWEB comes as two rows with the same range
export function otaChildAgesToFormValues(
	data:
		| {
				min_age: number
				max_age: number
				ages: THotelOtaChildAge[]
				aweb_remote_pax_type?: string | null
		  }
		| undefined
): TChildAgesFormValues {
	if (!data) {
		return { min_age: DEFAULT_MIN_AGE, max_age: DEFAULT_MAX_AGE, bands: [] }
	}
	const paxTypesByRange = new Map<
		string,
		{
			end_age: number
			paxTypes: Set<THotelOtaChildAgePaxType>
			remotePaxTypes: Partial<Record<THotelOtaChildAgePaxType, string>>
		}
	>()
	const sortedAges = [...data.ages].sort(
		(a, b) => a.start_age - b.start_age || a.end_age - b.end_age
	)
	for (const age of sortedAges) {
		const key = `${age.start_age}-${age.end_age}`
		const range = paxTypesByRange.get(key) || {
			end_age: age.end_age,
			paxTypes: new Set<THotelOtaChildAgePaxType>(),
			remotePaxTypes: {},
		}
		range.paxTypes.add(age.pax_type)
		if (age.remote_pax_type) {
			range.remotePaxTypes[age.pax_type] = age.remote_pax_type
		}
		paxTypesByRange.set(key, range)
	}
	const bands = Array.from(paxTypesByRange.values()).map(
		({ end_age, paxTypes, remotePaxTypes }): TChildAgeBand => ({
			end_age,
			occupancy: CHILD_AGE_BAND_OCCUPANCIES.find(
				(o) =>
					o.paxTypes.length === paxTypes.size &&
					o.paxTypes.every((paxType) => paxTypes.has(paxType))
			)?.value,
			cnb_remote_pax_type: remotePaxTypes.CNB,
			cweb_remote_pax_type: remotePaxTypes.CWEB,
		})
	)
	return {
		min_age: data.min_age,
		max_age: data.max_age,
		aweb_remote_pax_type: data.aweb_remote_pax_type || undefined,
		bands,
	}
}

// The mappings belong to one channel manager, so they go when it changes
export function withoutChildAgeRemotePaxTypes(
	childAges: TChildAgesFormValues
): TChildAgesFormValues {
	return {
		...childAges,
		aweb_remote_pax_type: undefined,
		bands: (childAges.bands || []).map((band) => ({
			...band,
			cnb_remote_pax_type: undefined,
			cweb_remote_pax_type: undefined,
		})),
	}
}

// Expects values that passed `childAgesValidationSchema`. Pass the hotel's
// `remotePaxTypes` to send its channel manager mappings too.
export function formValuesToOtaChildAgesPayload(
	values: TChildAgesFormValues,
	remotePaxTypes?: TRemotePaxTypeDefaults | null
): TOtaChildAgesPayload {
	const startAges = bandStartAges(values.min_age, values.bands)
	return {
		min_age: Number(values.min_age),
		max_age: Number(values.max_age),
		...(remotePaxTypes
			? {
					aweb_remote_pax_type: mappedRemotePaxType(
						values.aweb_remote_pax_type,
						remotePaxTypes.default_adult_remote_pax_type
					),
				}
			: {}),
		ages: values.bands.flatMap((band, index) =>
			occupancyPaxTypes(band.occupancy).map((pax_type) => ({
				pax_type,
				start_age: Number(startAges[index]),
				end_age: Number(band.end_age),
				...(remotePaxTypes
					? {
							remote_pax_type: mappedRemotePaxType(
								band[bandRemotePaxTypeField(pax_type)],
								remotePaxTypes.default_child_remote_pax_type
							),
						}
					: {}),
			}))
		),
	}
}

/**
 * The error to show when the bands don't reach the max age, or `undefined`
 * when they do. Having no bands at all is allowed.
 */
export function lastBandEndAgeError(
	values: TChildAgesFormValues
): string | undefined {
	const bands = values.bands || []
	const lastBand = bands[bands.length - 1]
	if (
		bands.length > 0 &&
		isWholeNumber(values.max_age) &&
		lastBand?.end_age !== values.max_age
	) {
		return `Last age band's End Age must be ${values.max_age}`
	}
	return undefined
}

// The child ages values may sit at the form's root or under a key (e.g. in the
// hotel onboarding form), so look up the nearest object that holds the bands.
function childAgesValuesOf(
	ancestors: readonly { value: unknown }[] | undefined
): TChildAgesFormValues | undefined {
	return ancestors?.find(
		(ancestor) =>
			ancestor.value &&
			typeof ancestor.value === "object" &&
			Array.isArray((ancestor.value as TChildAgesFormValues).bands)
	)?.value as TChildAgesFormValues | undefined
}

function bandIndexOf(path: string | undefined): number {
	const match = /bands\[(\d+)\]/.exec(path || "")
	return match ? Number(match[1]) : -1
}

const wholeNumberSchema = Validator.number()
	.integer("Please enter a whole number")
	.min(1, "Must be > 0")
	.required("Required")

export const childAgesValidationSchema = Validator.object()
	.shape({
		min_age: wholeNumberSchema,
		max_age: wholeNumberSchema
			.max(17, "Must be ≤ 17")
			.test("min-max-order", "", function (maxAge) {
				const { min_age: minAge } = this.parent
				if (!isWholeNumber(minAge) || !isWholeNumber(maxAge)) return true
				if (maxAge < minAge) {
					return this.createError({ message: `Should be at least ${minAge}` })
				}
				return true
			}),
		bands: Validator.array()
			.min(1, "At least one age band is required")
			.of(
				Validator.object().shape({
					end_age: wholeNumberSchema.test("band-range", "", function (endAge) {
						const root = childAgesValuesOf(this.from)
						const index = bandIndexOf(this.path)
						if (!root || index < 0 || !isWholeNumber(endAge)) return true
						const bands = root.bands || []
						const start = bandStartAges(root.min_age, bands)[index]
						if (isWholeNumber(start) && endAge < start) {
							return this.createError({
								message: `Must be ≥ ${start}`,
							})
						}
						return true
					}),
					occupancy: Validator.string().required("Required"),
				})
			),
	})
	.test("last-band-end-age", "", function (value) {
		const { min_age, max_age } = (value as TChildAgesFormValues) || {}
		if (
			!isWholeNumber(min_age) ||
			!isWholeNumber(max_age) ||
			min_age < 1 ||
			max_age > 17 ||
			max_age < min_age
		) {
			return true
		}
		const error = lastBandEndAgeError(value as TChildAgesFormValues)
		if (!error) return true
		return this.createError({
			path: this.path ? `${this.path}.bands` : "bands",
			message: error,
		})
	})

export function useDebounce<T>(value: T, delay = 500): T {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

// The seven days of the calendar week that starts on `from`
export function getCalendarDays(from: Date): Date[] {
	return Array.from({ length: 7 }, (_, index) => addUnit(from, index, "day"))
}
